import {
  ApartmentOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BlockOutlined,
  BranchesOutlined,
  CloudServerOutlined,
  CodeOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  DollarOutlined,
  GiftOutlined,
  GlobalOutlined,
  HddOutlined,
  HomeOutlined,
  ProjectOutlined,
  ReadOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SkinOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuDataItem } from '@ant-design/pro-components';
import { Link, useAccess, useIntl, useLocation } from '@umijs/max';
import { Drawer, Menu, type MenuProps } from 'antd';
import React from 'react';
import {
  getDomainEntryPath,
  getNavigationDomain,
  getNavigationItem,
  hasDomainAccess,
  hasNavigationAccess,
  type NavigationAccessChecker,
  type NavigationItem,
  navigationDomains,
} from '@/navigation';
import './index.less';

const iconComponents: Record<string, React.ComponentType> = {
  apartment: ApartmentOutlined,
  api: ApiOutlined,
  appstore: AppstoreOutlined,
  audit: AuditOutlined,
  bank: BankOutlined,
  block: BlockOutlined,
  branches: BranchesOutlined,
  cloudServer: CloudServerOutlined,
  code: CodeOutlined,
  creditCard: CreditCardOutlined,
  dashboard: DashboardOutlined,
  database: DatabaseOutlined,
  deploymentUnit: DeploymentUnitOutlined,
  dollar: DollarOutlined,
  gift: GiftOutlined,
  global: GlobalOutlined,
  hdd: HddOutlined,
  home: HomeOutlined,
  project: ProjectOutlined,
  read: ReadOutlined,
  robot: RobotOutlined,
  rocket: RocketOutlined,
  safetyCertificate: SafetyCertificateOutlined,
  setting: SettingOutlined,
  shop: ShopOutlined,
  shopping: ShoppingOutlined,
  skin: SkinOutlined,
  tags: TagsOutlined,
  team: TeamOutlined,
  thunderbolt: ThunderboltOutlined,
  tool: ToolOutlined,
  user: UserOutlined,
};

export function renderNavigationIcon(icon: string) {
  const Icon = iconComponents[icon] || AppstoreOutlined;
  return (
    <span className="workspace-navigation-icon" aria-hidden="true">
      <Icon />
    </span>
  );
}

/**
 * Items without a path are expandable groups: their key stays `${domain.id}:${id}`
 * and their children are rendered recursively.
 */
function buildNavigationMenuItems(
  domainId: string,
  items: NavigationItem[],
  formatMessage: (descriptor: { id: string }) => string,
  access?: NavigationAccessChecker,
): MenuDataItem[] {
  return items.flatMap<MenuDataItem>((item) => {
    if (item.planned || !hasNavigationAccess(item, access)) return [];
    const key = `${domainId}:${item.id}`;
    const name = formatMessage({ id: item.titleId });
    const icon = renderNavigationIcon(item.icon);

    if (item.path) {
      return [{ key, path: item.path, name, icon }];
    }

    const children = buildNavigationMenuItems(
      domainId,
      item.children ?? [],
      formatMessage,
      access,
    );
    if (!children.length) return [];
    return [{ key, name, icon, children }];
  });
}

export function buildWorkspaceMenu(
  formatMessage: (descriptor: { id: string }) => string,
  access?: NavigationAccessChecker,
): MenuDataItem[] {
  return navigationDomains
    .filter((domain) => hasDomainAccess(domain, access))
    .map((domain) => ({
      key: domain.id,
      path: getDomainEntryPath(domain, access),
      name: formatMessage({ id: domain.titleId }),
      icon: renderNavigationIcon(domain.icon),
      children: buildNavigationMenuItems(
        domain.id,
        domain.items,
        formatMessage,
        access,
      ),
    }));
}

/** Keys of the expandable groups that contain the active item, outermost first. */
function findActiveGroupKeys(
  items: NavigationItem[],
  activeItemId?: string,
): string[] {
  if (!activeItemId) return [];
  for (const item of items) {
    const children = item.children ?? [];
    if (!children.length) continue;
    if (children.some((child) => child.id === activeItemId)) return [item.id];
    const nested = findActiveGroupKeys(children, activeItemId);
    if (nested.length) return [item.id, ...nested];
  }
  return [];
}

type MobileMenuItem = NonNullable<MenuProps['items']>[number];

const buildMobileMenuItems = (
  items: MenuDataItem[] | undefined,
  onClose: () => void,
): MobileMenuItem[] =>
  (items ?? []).map((item) => {
    const children = item.children?.length
      ? buildMobileMenuItems(item.children, onClose)
      : undefined;
    return {
      key: item.key || '',
      icon: item.icon,
      label: children ? (
        item.name
      ) : (
        <Link to={item.path || '/home'} onClick={onClose}>
          {item.name}
        </Link>
      ),
      ...(children ? { children } : {}),
    };
  });

/** Avoid the bundled ProLayout drawer's close-on-opening animation callback. */
export const MobileWorkspaceMenu: React.FC<{
  open: boolean;
  onClose: () => void;
  siderWidth: number;
}> = ({ open, onClose, siderWidth }) => {
  const intl = useIntl();
  const location = useLocation();
  const access = useAccess();
  const currentLocation = `${location.pathname}${location.search}`;
  const domain = getNavigationDomain(currentLocation);
  const activeItem = getNavigationItem(currentLocation);
  const activeGroupKeys = React.useMemo(
    () =>
      findActiveGroupKeys(domain.items, activeItem?.id).map(
        (id) => `${domain.id}:${id}`,
      ),
    [domain.id, domain.items, activeItem?.id],
  );
  const [openKeys, setOpenKeys] = React.useState<string[]>(() => [
    domain.id,
    ...activeGroupKeys,
  ]);

  React.useEffect(() => {
    setOpenKeys([domain.id, ...activeGroupKeys]);
  }, [domain.id, activeGroupKeys]);

  const menuItems: MenuProps['items'] = buildWorkspaceMenu(
    intl.formatMessage,
    access,
  ).map((group) => ({
    key: group.key || '',
    icon: group.icon,
    label: group.name,
    children: buildMobileMenuItems(group.children, onClose),
  }));

  return (
    <Drawer
      rootClassName="workspace-mobile-drawer"
      placement="left"
      open={open}
      onClose={onClose}
      title={intl.formatMessage({ id: 'pages.navigation.mainNavigation' })}
      size={Math.min(Math.max(siderWidth, 260), 320)}
      getContainer={false}
      styles={{ body: { padding: '12px 0' } }}
    >
      <Menu
        mode="inline"
        items={menuItems}
        selectedKeys={[`${domain.id}:${activeItem?.id || 'overview'}`]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
      />
    </Drawer>
  );
};
